import {setEquals} from "./SetHelpers";
import type {NonOptional, NonOptionalNullable, Nullable, Optional, OptionalNullable} from "./TypeHelpers";
import {isNonOptionalNullable} from "./TypeHelpers";

export interface IEquatable {
  equals(other: this): boolean;
}

export function isEquatable(value: unknown): value is IEquatable {
  if (!(typeof value === "object" && value !== null && "equals" in value)) return false;
  if (typeof value.equals !== "function") return false;
  // `this` does not count as arg!
  return value.equals.length === 1;
}

type EqualityFn<T> = (lhs: T, rhs: T) => boolean;
type LiteralEquatable = string | number | boolean | bigint | symbol;

function isLiteralEquatable(value: unknown): value is LiteralEquatable {
  return typeof value === "string"
    || typeof value === "number"
    || typeof value === "boolean"
    || typeof value === "bigint"
    || typeof value === "symbol";
}

export function identityEquals<T>(lhs: T, rhs: T): boolean {
  return lhs === rhs;
}

function _anyEquals<T>(lhs: OptionalNullable<T>, rhs: OptionalNullable<T>, comparer?: EqualityFn<NonOptionalNullable<T>>): boolean {
  // Objects might not be considered equal!
  if (typeof lhs !== "object" && lhs === rhs) return true;
  // If one of them is null/undefined, check if both are (===).
  if (!isNonOptionalNullable(lhs) || !isNonOptionalNullable(rhs)) return lhs === rhs;
  if (comparer) return comparer(lhs, rhs);
  if (isLiteralEquatable(lhs) && isLiteralEquatable(rhs)) return lhs === rhs;
  // We could assume the conformance here - the overloads would make sure of it.
  if (isEquatable(lhs) && isEquatable(rhs)) return lhs.equals(rhs);
  return false;
}

export function equals<T extends LiteralEquatable>(lhs: OptionalNullable<T>, rhs: OptionalNullable<T>): boolean;
export function equals<T extends IEquatable>(lhs: OptionalNullable<T>, rhs: OptionalNullable<T>): boolean;
export function equals<T>(lhs: OptionalNullable<T>,
                          rhs: OptionalNullable<T>,
                          comparer: EqualityFn<NonOptionalNullable<T>>): boolean
export function equals<T>(lhs: Optional<T>, rhs: Optional<T>, comparer: EqualityFn<NonOptional<T>>): boolean
export function equals<T>(lhs: Nullable<T>, rhs: Nullable<T>, comparer: EqualityFn<NonNullable<T>>): boolean
export function equals<T>(lhs: OptionalNullable<T>,
                          rhs: OptionalNullable<T>,
                          comparer?: EqualityFn<NonOptionalNullable<T>>): boolean {
  return _anyEquals(lhs, rhs, comparer);
}

export function allEqualsOrdered<T extends LiteralEquatable>(lhs: OptionalNullable<readonly T[]>,
                                                             rhs: OptionalNullable<readonly T[]>): boolean;
export function allEqualsOrdered<T extends IEquatable>(lhs: OptionalNullable<readonly T[]>,
                                                       rhs: OptionalNullable<readonly T[]>): boolean;
export function allEqualsOrdered<T>(lhs: OptionalNullable<readonly T[]>,
                                    rhs: OptionalNullable<readonly T[]>,
                                    elementComparer: EqualityFn<T>): boolean
export function allEqualsOrdered<T>(lhs: OptionalNullable<readonly T[]>,
                                    rhs: OptionalNullable<readonly T[]>,
                                    elementComparer?: EqualityFn<T>): boolean {
  return equals(lhs, rhs, (l, r) => l.length === r.length && l.every((v, i) => _anyEquals(v, r[i], elementComparer)));
}

export function allEqualsUnordered<T extends LiteralEquatable>(lhs: OptionalNullable<readonly T[]>,
                                                               rhs: OptionalNullable<readonly T[]>): boolean;
export function allEqualsUnordered<T extends IEquatable>(lhs: OptionalNullable<readonly T[]>,
                                                         rhs: OptionalNullable<readonly T[]>): boolean;
export function allEqualsUnordered<T>(lhs: OptionalNullable<readonly T[]>,
                                      rhs: OptionalNullable<readonly T[]>,
                                      elementComparer: EqualityFn<T>): boolean
export function allEqualsUnordered<T>(lhs: OptionalNullable<readonly T[]>,
                                      rhs: OptionalNullable<readonly T[]>,
                                      elementComparer?: EqualityFn<T>): boolean {
  return equals(lhs, rhs, (l, r) => l.length === r.length && l.every(lv => r.some(rv => _anyEquals(lv, rv, elementComparer))));
}

export function allEqualsUnorderedViaSet<T extends LiteralEquatable>(lhs: OptionalNullable<Readonly<Iterable<T>>>,
                                                                     rhs: OptionalNullable<Readonly<Iterable<T>>>): boolean {
  return equals(lhs, rhs, (l, r) => setEquals(new Set(l), new Set(r)));
}

export function mapEquals<
  TKey extends LiteralEquatable,
  TValue extends LiteralEquatable
>(lhs: ReadonlyMap<TKey, TValue>, rhs: ReadonlyMap<TKey, TValue>): boolean;
export function mapEquals<TKey extends LiteralEquatable, TValue extends IEquatable>(lhs: ReadonlyMap<TKey, TValue>,
                                                                                    rhs: ReadonlyMap<TKey, TValue>): boolean;
export function mapEquals<TKey extends LiteralEquatable, TValue>(lhs: ReadonlyMap<TKey, TValue>,
                                                                 rhs: ReadonlyMap<TKey, TValue>,
                                                                 valueComparer: EqualityFn<TValue>): boolean
export function mapEquals<TKey extends LiteralEquatable, TValue>(lhs: ReadonlyMap<TKey, TValue>,
                                                                 rhs: ReadonlyMap<TKey, TValue>,
                                                                 valueComparer?: EqualityFn<TValue>): boolean {
  if (lhs.size !== rhs.size || !allEqualsUnorderedViaSet(lhs.keys(), rhs.keys()))
    return false;
  for (const [key, value] of lhs)
    if (!_anyEquals(value, rhs.get(key), valueComparer))
      return false;
  return true;
}
